// src/components/workflow/WorkflowSummaryCard.tsx

import React from 'react';
import { CheckCircle, Shield, AlertTriangle, Users, Target, ArrowUpRight } from 'lucide-react';
import { Card, ProgressBar, LogoLoadingMark } from '../ui';

interface WorkflowSummaryData {
  workflow_config: {
    id: number;
    name: string;
    source_unit_type: string;
    max_resolution_level: string;
    step_count: number;
  } | null;
  current_step: number | null;
  total_steps: number;
  completed: number;
  resolution: string | null;
  needs_rapim: boolean | null;
  steps: Array<{
    step_order: number;
    step_label: string | null;
    can_resolve_here: boolean;
    validator: { id: number; name: string } | null;
    validator_role: string | null;
    status: string;
    feedback: string | null;
    action_at: string | null;
  }>;
}

interface Props {
  summary: WorkflowSummaryData | null;
  loading?: boolean;
}

const RESOLUTION_LABELS: Record<string, string> = {
  fakultas: 'Fakultas',
  lembaga: 'Lembaga',
  warek: 'Wakil Rektor',
  universitas: 'Rapat Pimpinan',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  approved: 'bg-success/10 text-success',
  rejected: 'bg-danger/10 text-danger',
  resolved: 'bg-emerald-100 text-emerald-700',
  skipped: 'bg-gray-50 text-gray-400',
};

const WorkflowSummaryCard: React.FC<Props> = ({ summary, loading }) => {
  if (loading) {
    return (
      <Card>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-2 bg-gray-200 rounded w-full" />
          <div className="h-2 bg-gray-200 rounded w-2/3" />
        </div>
      </Card>
    );
  }

  if (!summary || !summary.workflow_config) {
    return null;
  }

  const progressPercent = summary.total_steps > 0
    ? Math.round((summary.completed / summary.total_steps) * 100)
    : 0;

  const isFullyCompleted = summary.completed === summary.total_steps;
  const isResolved = !!summary.resolution && !summary.needs_rapim;

  return (
    <Card borderColor="border-l-info">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-3 flex items-center gap-2">
        <Target className="w-4 h-4" />
        Alur Validasi Workflow
      </h3>

      {/* Config Info */}
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-0.5 bg-info/10 text-info text-[10px] font-bold rounded-full">
          {summary.workflow_config.source_unit_type}
        </span>
        <span className="text-xs text-neutral-text-primary font-medium">
          {summary.workflow_config.name}
        </span>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-neutral-text-secondary">
            Validasi {summary.completed}/{summary.total_steps}
          </span>
          <span className="font-bold text-teal-main">{progressPercent}%</span>
        </div>
        <ProgressBar value={progressPercent} color="teal" size="sm" />
      </div>

      {/* Steps Timeline */}
      <div className="space-y-2">
        {summary.steps.map((step) => (
          <div
            key={step.step_order}
            className={`flex items-center gap-3 p-2 rounded-lg ${
              step.status === 'pending' ? 'bg-warning/5 border border-warning/20' :
              step.status === 'approved' ? 'bg-success/5' :
              step.status === 'resolved' ? 'bg-emerald-50' :
              step.status === 'rejected' ? 'bg-danger/5' :
              'bg-neutral-bg/30'
            }`}
          >
            {/* Status Icon */}
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
              step.status === 'approved' ? 'bg-success text-white' :
              step.status === 'resolved' ? 'bg-emerald-500 text-white' :
              step.status === 'rejected' ? 'bg-danger text-white' :
              step.status === 'pending' ? 'bg-warning/20 text-warning' :
              'bg-gray-200 text-gray-400'
            }`}>
              {step.status === 'approved' && <CheckCircle className="w-3.5 h-3.5" />}
              {step.status === 'resolved' && <Shield className="w-3.5 h-3.5" />}
              {step.status === 'rejected' && <AlertTriangle className="w-3.5 h-3.5" />}
              {step.status === 'pending' && <LogoLoadingMark size="xs" />}
              {step.status === 'skipped' && <span className="text-[9px]">—</span>}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-neutral-text-primary">
                  {step.step_label || `Step ${step.step_order}`}
                </span>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${STATUS_COLORS[step.status] || ''}`}>
                  {step.status === 'approved' ? 'Setuju' :
                   step.status === 'resolved' ? 'Selesai Lokal' :
                   step.status === 'rejected' ? 'Ditolak' :
                   step.status === 'pending' ? 'Menunggu' :
                   step.status === 'skipped' ? 'Dilewati' : step.status}
                </span>
              </div>
              {step.validator && (
                <p className="text-[10px] text-neutral-text-secondary mt-0.5 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {step.validator.name}
                  {step.action_at && ` • ${new Date(step.action_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}`}
                </p>
              )}
              {step.feedback && (
                <p className="text-[10px] text-neutral-text-secondary mt-1 italic line-clamp-2">
                  "{step.feedback}"
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Resolution Info */}
      {isResolved && (
        <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" />
            Diselesaikan di tingkat {RESOLUTION_LABELS[summary.resolution || ''] || summary.resolution}
          </p>
        </div>
      )}

      {/* Needs RAPIM */}
      {isFullyCompleted && summary.needs_rapim && (
        <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-xs font-semibold text-purple-700 flex items-center gap-1">
            <ArrowUpRight className="w-3.5 h-3.5" />
            Menunggu pembahasan di RAPIM Universitas
          </p>
        </div>
      )}

      {/* Max Resolution Level */}
      <div className="mt-3 text-[10px] text-neutral-text-secondary">
        Level maks: {RESOLUTION_LABELS[summary.workflow_config.max_resolution_level] || summary.workflow_config.max_resolution_level}
      </div>
    </Card>
  );
};

export default WorkflowSummaryCard;
